import { CSSObject } from '@emotion/react';
import create from 'zustand';
import { isDevice } from './utils';

type UseScrollLockState = {
    // The current open state
    isLocked: boolean;

    // Specify the open state
    setIsLocked: (isLocked: boolean) => void;

    // Locked scroll position
    scrollY: number;

    lock: () => void;
    unlock: () => void;
};

const deviceLockStyles: CSSObject = {
    overflow: 'hidden',
    height: '100%',
    touchAction: 'none',
};
const deviceUnlockStyles: CSSObject = {
    overflow: 'unset',
    height: 'auto',
    touchAction: 'unset',
};

const desktopLockStyles: CSSObject = {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    overflowY: 'scroll',
    height: '100%',
};

const scrollUnlockStyles = {
    overflow: '',
    touchAction: '',
    position: '',
    top: '',
    bottom: '',
    left: '',
    right: '',
};

let initialHashHandled = false;

export const useScrollLock = create<UseScrollLockState>((set, get) => ({
    isLocked: false,
    setIsLocked: (isLocked: boolean) => set({ isLocked }),
    scrollY: 0,
    lock: () => {
        const scrollY = window.scrollY;
        const doc = document.documentElement;
        const body = document.body;

        set({ isLocked: true, scrollY });

        if (isDevice()) {
            Object.assign(doc.style, deviceLockStyles);
            Object.assign(body.style, deviceLockStyles);
            return;
        }

        Object.assign(doc.style, desktopLockStyles);
        doc.style.top = `-${scrollY}px`;
    },

    unlock: () => {
        const state = get();
        const doc = document.documentElement;
        const body = document.body;

        set({ isLocked: false, scrollY: 0 });

        Object.assign(doc.style, scrollUnlockStyles);

        if (isDevice()) {
            Object.assign(doc.style, deviceUnlockStyles);
            Object.assign(body.style, deviceUnlockStyles);
            return;
        }

        const hash = window.location.hash;

        if (hash && state.scrollY === 0 && !initialHashHandled) {
            initialHashHandled = true;

            requestAnimationFrame(() => {
                document.querySelector(hash)?.scrollIntoView({
                    behavior: 'auto',
                    block: 'start',
                });
            });
            return;
        }

        window.scrollTo(0, state.scrollY);
    },
}));
